import {
  AfterViewInit,
  Component,
  Input,
  OnChanges,
  OnInit
} from '@angular/core';

type PinterestUrlType =
  | 'pin'
  | 'board'
  | 'profile'
  | 'invite'
  | 'system'
  | 'unknown'
  | 'invalid';

interface PinterestRenderUrl {
  originalUrl: string;
  renderUrl: string;
  type: PinterestUrlType;
  embeddable: boolean;

  fallback?: boolean;
  message?: string;
}

@Component({
  selector: 'app-pinterest-board',
  templateUrl: './pinterest-board.component.html',
  styleUrls: ['./pinterest-board.component.less']
})
export class PinterestBoardComponent
  implements OnInit, OnChanges, AfterViewInit {

  @Input() isHandset = false;
  @Input() url = '';

  pinterestItems: PinterestRenderUrl[] = [];

  pagewidth: number;

  private viewInitialized = false;

  private readonly SYSTEM_PAGES = [
    'login',
    'logout',
    'signup',
    'search',
    'ideas',
    'today',
    'explore',
    'settings',
    'business',
    'help',
    'invited'
  ];

  constructor() {
    this.pagewidth = window.innerWidth;
  }

  ngOnInit(): void {
    this.preparePinterestUrls();
  }

  ngOnChanges(): void {
    this.preparePinterestUrls();

    if (this.viewInitialized) {
      this.buildPinterestWidgets();
    }
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.buildPinterestWidgets();
  }

  get boardUrlList(): PinterestRenderUrl[] {
    return this.pinterestItems.filter(item =>
      item.type === 'board' &&
      item.embeddable &&
      !item.fallback
    );
  }

  get pinUrlList(): PinterestRenderUrl[] {
    return this.pinterestItems.filter(item =>
      item.type === 'pin' &&
      item.embeddable &&
      !item.fallback
    );
  }

  get profileUrlList(): PinterestRenderUrl[] {
    return this.pinterestItems.filter(item =>
      item.type === 'profile' &&
      item.embeddable &&
      !item.fallback
    );
  }

  get fallbackUrlList(): PinterestRenderUrl[] {
    return this.pinterestItems.filter(item =>
      item.fallback ||
      !item.embeddable
    );
  }

  private get embeddableUrlList(): PinterestRenderUrl[] {
    return [
      ...this.boardUrlList,
      ...this.pinUrlList,
      ...this.profileUrlList
    ];
  }

  private buildPinterestWidgets(): void {

    setTimeout(() => {

      if (
        window['PinUtils'] &&
        typeof window['PinUtils'].build === 'function'
      ) {
        window['PinUtils'].build();
      }

      this.detectEmbedFailures();

    }, 500);

  }

  private detectEmbedFailures(): void {

    setTimeout(() => {

      const embeds = document.querySelectorAll(
        '.pinterest-embed'
      );

      const embeddableItems =
        this.embeddableUrlList;

      embeds.forEach((element, index) => {

        const iframe =
          element.querySelector('iframe');

        const hasPinterestContent =
          iframe ||
          element.querySelector('span') ||
          element.querySelector('img');

        if (!hasPinterestContent) {

          const item =
            embeddableItems[index];

          if (
            item &&
            item.embeddable
          ) {
            item.fallback = true;
            item.message =
              'Pinterest content could not be embedded. Open the original link to view it directly.';
          }

        }

      });

    }, 8000);

  }

  getPinDo(item: PinterestRenderUrl): string {

    if (item.type === 'pin') {
      return 'embedPin';
    }

    if (item.type === 'profile') {
      return 'embedUser';
    }

    return 'embedBoard';
  }

  normalizePinterestUrl(
    rawUrl: string
  ): PinterestRenderUrl {

    const trimmedUrl =
      (rawUrl || '').trim();

    if (!trimmedUrl) {
      return {
        originalUrl: '',
        renderUrl: '',
        type: 'invalid',
        embeddable: false
      };
    }

    try {

      const url =
        new URL(trimmedUrl);

      const hostname =
        url.hostname.toLowerCase();

      if (
        hostname !== 'pinterest.com' &&
        !hostname.endsWith('.pinterest.com')
      ) {
        return {
          originalUrl: trimmedUrl,
          renderUrl: trimmedUrl,
          type: 'unknown',
          embeddable: false,
          fallback: true,
          message:
            'Not a Pinterest URL.'
        };
      }

      const originalPath =
        url.pathname.replace(/\/+/g, '/');

      if (
        originalPath
          .toLowerCase()
          .startsWith('/invited')
      ) {

        return {
          originalUrl: trimmedUrl,
          renderUrl: trimmedUrl,
          type: 'invite',
          embeddable: false,
          fallback: true,
          message:
            'Pinterest invite links cannot be embedded. Open the original link to access the board.'
        };

      }

      url.search = '';
      url.hash = '';

      let path =
        url.pathname.replace(/\/+/g, '/');

      path =
        path.replace(/\/$/, '');

      const segments =
        path.split('/').filter(Boolean);

      const firstSegment =
        segments[0]?.toLowerCase();

      if (
        firstSegment &&
        this.SYSTEM_PAGES.includes(firstSegment)
      ) {

        return {
          originalUrl: trimmedUrl,
          renderUrl: trimmedUrl,
          type: 'system',
          embeddable: false,
          fallback: true,
          message:
            'Pinterest system pages cannot be embedded.'
        };

      }

      let type: PinterestUrlType =
        'unknown';

      let embeddable =
        false;

      let message = '';

      if (
        segments.length >= 2 &&
        firstSegment === 'pin'
      ) {

        type = 'pin';
        embeddable = true;

      }
      else if (
        segments.length === 2 &&
        firstSegment !== 'pin'
      ) {

        type = 'board';
        embeddable = true;

      }
      else if (
        segments.length === 1
      ) {

        type = 'profile';
        embeddable = true;
      }

      return {
        originalUrl: trimmedUrl,
        renderUrl:
          `${url.origin}/${segments.join('/')}/`,
        type,
        embeddable,
        fallback: !embeddable,
        message
      };

    } catch {

      return {
        originalUrl: trimmedUrl,
        renderUrl: trimmedUrl,
        type: 'invalid',
        embeddable: false,
        fallback: true,
        message:
          'Invalid Pinterest URL.'
      };

    }

  }

  private preparePinterestUrls(): void {

    this.pinterestItems =
      this.url
        .split(/[,;]+/)
        .map(url =>
          this.normalizePinterestUrl(url)
        )
        .filter(x => x.originalUrl);

  }

}
